import { Button } from '@/components/ui/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/Command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import {
  AspNetUsers,
  AuditLogInsertInput,
  Device,
  UserDeviceLinkInsertInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { cn } from '@/lib/utils';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { Role } from '@/models/role.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { bulkAssignUserToDevicesMutation } from '../graphql/mutation';
import {
  userByRoleQuery,
  userDeviceLinksByUserAndDevicesQuery,
} from '../graphql/query';

interface BulkAssignUserDialogProps {
  open: boolean;
  /** The devices currently shown on the list page (one page of filtered results). */
  devices: Device[];
  onToggleOpen: (shouldRefetch?: boolean) => void;
}

const pluralDevices = (count: number) =>
  `${count} device${count === 1 ? '' : 's'}`;

export function BulkAssignUserDialog({
  open,
  devices,
  onToggleOpen,
}: BulkAssignUserDialogProps) {
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['UserByRole'],
    queryFn: () =>
      execute({
        query: userByRoleQuery,
        variables: {
          role: Role.INSTALLER,
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: open,
  });

  const users = (usersData?.aspNetUsers ?? []) as AspNetUsers[];
  const selectedUser = users.find((user) => user.id === selectedUserId);

  // Devices already linked to the chosen user are left out of the insert so
  // the summary and the resulting count reflect what will actually change.
  const { devicesToAssign, alreadyAssignedCount } = useMemo(() => {
    if (!selectedUserId) {
      return { devicesToAssign: devices, alreadyAssignedCount: 0 };
    }
    const toAssign = devices.filter(
      (device) =>
        !(device.userDeviceLinks ?? []).some(
          (link) => link.aspNetUser?.id === selectedUserId,
        ),
    );
    return {
      devicesToAssign: toAssign,
      alreadyAssignedCount: devices.length - toAssign.length,
    };
  }, [devices, selectedUserId]);

  const { mutateAsync: bulkAssignUser } = useMutation({
    mutationKey: ['BulkAssignUserToDevices'],
    mutationFn: (variables: {
      inputs: UserDeviceLinkInsertInput[];
      auditLogInput: AuditLogInsertInput;
    }) =>
      execute({
        query: bulkAssignUserToDevicesMutation,
        variables,
      }),
  });

  const handleOpenChange = () => {
    if (isSubmitting) {
      return;
    }
    onToggleOpen();
  };

  const handleAssign = async () => {
    if (!selectedUserId || devicesToAssign.length === 0) {
      return;
    }
    const userName = selectedUser?.userName ?? 'this user';
    setIsSubmitting(true);

    try {
      // The page data can be stale and the insert has no ON CONFLICT guard
      // (the user/device uniqueness is a unique index, not a constraint), so
      // re-check existing links on the server right before inserting.
      let linkedDeviceIds: Set<string>;
      try {
        const existing = await execute({
          query: userDeviceLinksByUserAndDevicesQuery,
          variables: {
            userId: selectedUserId,
            deviceIds: devicesToAssign.map((device) => device.id),
          },
        });
        linkedDeviceIds = new Set(
          (existing.userDeviceLink ?? []).map((link) => link.deviceId),
        );
      } catch (error) {
        console.error('Could not check existing user assignments', error);
        toast.error('Could not check existing assignments. Please try again.');
        return;
      }

      const newDevices = devicesToAssign.filter(
        (device) => !linkedDeviceIds.has(device.id),
      );
      if (newDevices.length === 0) {
        toast.info(
          `All devices on this page are already assigned to ${userName}`,
          {
            duration: 3000,
          },
        );
        onToggleOpen(true);
        return;
      }

      const timestamp = new Date().toISOString();
      const inputs = newDevices.map(
        (device): UserDeviceLinkInsertInput => ({
          id: uuid(),
          userId: selectedUserId,
          deviceId: device.id,
          createdAt: timestamp,
          updatedAt: timestamp,
        }),
      );

      try {
        const result = await bulkAssignUser({
          inputs,
          auditLogInput: createAuditLogInput({
            action: AuditLogAction.ASSIGN_USER_TO_DEVICES_BATCH,
            entities: [
              { id: selectedUserId, entity: AuditLogEntityType.USER },
              ...newDevices.map((device) => ({
                id: device.id,
                entity: AuditLogEntityType.DEVICE,
              })),
            ],
          }),
        });

        const assignedCount =
          result.insertUserDeviceLink?.affectedRows ?? inputs.length;
        toast.success(
          `${pluralDevices(assignedCount)} assigned to ${userName}`,
          { duration: 3000 },
        );
        onToggleOpen(true);
      } catch (error) {
        // The global mutation error handler already shows a toast.
        console.error('Bulk user assignment failed', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Assign User to Devices</DialogTitle>
          <DialogDescription>
            Grant one user access to the {pluralDevices(devices.length)} listed
            on this page. Change the filters or page size first to adjust which
            devices are included.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label htmlFor="bulk-assign-user">User</Label>
          <Popover
            open={userPickerOpen}
            onOpenChange={setUserPickerOpen}
            modal={true}
          >
            <PopoverTrigger asChild>
              <Button
                id="bulk-assign-user"
                variant="outline"
                role="combobox"
                aria-expanded={userPickerOpen}
                className="w-full justify-between"
                disabled={isSubmitting}
              >
                {selectedUser ? (
                  selectedUser.userName
                ) : (
                  <span className="text-muted-foreground">
                    {isLoadingUsers ? 'Loading users...' : 'Select user'}
                  </span>
                )}
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
              <Command>
                <CommandInput placeholder="Search user..." className="h-9" />
                <CommandList>
                  <CommandEmpty>Not found.</CommandEmpty>
                  <CommandGroup>
                    {users.map((user) => (
                      <CommandItem
                        key={user.id}
                        // cmdk filters on `value`, so use the searchable
                        // username rather than the user id.
                        value={user.userName ?? user.id}
                        onSelect={() => {
                          setSelectedUserId(user.id);
                          setUserPickerOpen(false);
                        }}
                      >
                        {user.userName}
                        <Check
                          className={cn(
                            'ml-auto',
                            user.id === selectedUserId
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedUser && (
            <p className="text-muted-foreground text-sm">
              {devicesToAssign.length > 0
                ? `${pluralDevices(devicesToAssign.length)} will be assigned to ${selectedUser.userName}.`
                : `All devices on this page are already assigned to ${selectedUser.userName}.`}
              {alreadyAssignedCount > 0 &&
                devicesToAssign.length > 0 &&
                ` ${alreadyAssignedCount} already assigned and will be skipped.`}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onToggleOpen()}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              !selectedUserId || devicesToAssign.length === 0 || isSubmitting
            }
            onClick={() => void handleAssign()}
          >
            {isSubmitting && <Spinner className="size-4 text-current" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
